/*
 * Developed by CQ Inversiones SAS.  Copyright (c) 2024. All rights reserved.
 * Desarrollado por CQ Inversiones SAS.  Copyright (c) 2024. Todos los derechos reservados.
 */

/*
* Project:      frontend
* Developed by: Jhoana Marcela Rodriguez Fernandez
* Date:         6/4/2024 - 11:00
* Module name:  frontend
* File name:    mkad-contacts.service.ts
* IDE:          WebStorm
*/
import {Injectable} from '@angular/core';
import {map, Observable} from 'rxjs';
import {MkadBaseService, URLS_SERVICE} from '@shared/services';

import {ContactFormInterface, ContactListInterface} from '../../../interface';
import {HttpResponse} from '@angular/common/http';
import {HTTP_CODE_SUCCESS} from '@shared/constants';

export interface CodeDialInterface {
  name: string,
  code_dial: string
}

/**
 * service that handles contact endpoints
 * @extends MkadBaseService
 */
@Injectable()
export class MkadContactsService extends MkadBaseService {
  readonly coreEndpoints = URLS_SERVICE.core;
  readonly coreBase = this.coreEndpoints.base;
  readonly customer = URLS_SERVICE.meerkad.customer;
  readonly contact = URLS_SERVICE.meerkad.contacts;
  readonly group = URLS_SERVICE.meerkad.group;

  /**
   * @description
   * Method that queries the nested list of contacts
   */
  list(): Observable<ContactListInterface[]> {
    return this.http.post<ContactListInterface[]>(this.customer.base + this.contact.base + this.contact.list);
  }

  /**
   * @description
   * Method that sends a request to create a contact
   * @param payload payload for the endpoint
   */
  create(payload: ContactFormInterface): Observable<ContactFormInterface> {
    return this.http.post<ContactFormInterface>(this.customer.base + this.contact.base + this.contact.create, payload);
  }

  /**
   * @description
   * Method that sends a request to obtain the data of a contact
   * @param id contract id
   */
  getById(id: number): Observable<ContactFormInterface> {
    return this.http.post<ContactFormInterface>(this.customer.base + this.contact.base + this.contact.get, {id});
  }


  /**
   * @description
   * Method that sends a request to update a contact
   * @param payload payload for the endpoint
   */
  update(payload: ContactFormInterface): Observable<ContactFormInterface> {
    return this.http.post<ContactFormInterface>(this.customer.base + this.contact.base + this.contact.update, payload);
  }

  /**
   * @description
   * Method that sends a request to delete a contact
   * @param payload payload for the endpoint
   */
  delete(payload: number): Observable<boolean> {
    return this.http.post<HttpResponse<boolean>>(this.customer.base + this.contact.base + this.contact.delete, {id: payload}).pipe(
      map((prevResponse) => {
          if (!prevResponse) return true;
          return prevResponse.status === HTTP_CODE_SUCCESS;
        }
      )
    );
  }

  /**
   * @description
   * Method that sends a request to obtain the data of a code country
   */
  getCodeDial(): Observable<CodeDialInterface[]> {
    return this.http.post<{ name: string, dial_code: string }[]>(this.coreBase + this.coreEndpoints.dialCodes).pipe(
      map((response) => {
        return response.map(({name, dial_code}) => ({name: name, code_dial: dial_code}));
      }),
    );
  }

}
